import { AnimatedSprite, Texture } from 'pixi.js';
import { FSM, Store, TextureFactory } from '../../core';
import { IEventEmitter, Point, StatefulAnimation, StateMap, Vector } from '../../interface';
import { BaseObject } from '..';
import TextureInfo_1 from './Cucumber_1.json';
import TextureInfo_2 from './Cucumber_2.json';
import TextureInfo_3 from './Cucumber_3.json';
import TextureInfo_4 from './Cucumber_4.json';
import { PowerProgressBar } from '../../ui';
import { sound } from '@pixi/sound';

interface IMonster extends IEventEmitter {
  velocity: Vector;
  position: Point;
}

class Cucumber extends BaseObject {
  private fsm: FSM<IMonster>;
  private statefulAnimation: StatefulAnimation;
  public animatedSprite: AnimatedSprite;
  public health: number;
  public maxHealth: number;
  private progress: PowerProgressBar;
  public attackDT: number;
  public maxAttackDT: number;
  public currentFrame: number = 0;
  public attackProcess: number = 0;

  constructor() {
    super();
    const textures_1 = TextureFactory.instance.load(TextureInfo_1);
    const textures_2 = TextureFactory.instance.load(TextureInfo_2);
    const textures_3 = TextureFactory.instance.load(TextureInfo_3);
    const textures_4 = TextureFactory.instance.load(TextureInfo_4);

    this.statefulAnimation = {
      IDLE: {
        textures: textures_4.slice(20, 33),
        animationSpeed: 0.4,
        offset: { x: 0, y: 0 },
        loop: true,
      },
      LAST_HIT: {
        textures: textures_4.slice(0, 19),
        animationSpeed: 0.06,
        offset: { x: 0, y: 0 },
        loop: false,
      },
      HIT: {
        textures: textures_4.slice(0, 19),
        animationSpeed: 0.4,
        offset: { x: 0, y: 0 },
        loop: false,
      },
      DIE: {
        textures: textures_2.slice(0, 37),
        animationSpeed: 0.4,
        offset: { x: 0, y: 0 },
        loop: false,
      },
      ATTACK1: {
        textures: textures_1.slice(0, 74),
        animationSpeed: 0.4,
        offset: { x: 0, y: 0 },
        loop: false,
      },
      ATTACK2: {
        textures: textures_3.slice(0, 57),
        animationSpeed: 0.25,
        offset: { x: 0, y: 0 },
        loop: false,
      },
    };

    this.animatedSprite = new AnimatedSprite([Texture.EMPTY]);
    this.animatedSprite.scale.set(1);
    this.animatedSprite.updateAnchor = true;
    this.fsm = new FSM<IMonster>(this, this.states, 'IDLE');
    this.animatedSprite.onComplete = () => {
      this.fsm.trigger('ANIMATION_DONE');
    };
    this.animatedSprite.onFrameChange = (currentFrame: number) => {
      if (this.state === 'ATTACK1' && this.currentFrame < 31 && currentFrame >= 31) {
        this.emit('PROJECTILE', 1);
      } else if (this.state === 'ATTACK2' && this.currentFrame < 18 && currentFrame >= 18) {
        this.emit('PROJECTILE', 2);
      } else if (this.state === 'ATTACK2' && this.currentFrame < 23 && currentFrame >= 23) {
        this.emit('PROJECTILE', 3);
      }
      this.currentFrame = currentFrame;
    };
    this.stage.addChild(this.animatedSprite);

    this.maxHealth = 50;
    this.health = this.maxHealth;

    this.attackDT = 250;
    this.maxAttackDT = 300;

    this.progress = new PowerProgressBar(this.attackDT, this.maxAttackDT);
    this.progress.scale.set(2);
    this.progress.position.set(0, 40);
    this.container.addChild(this.progress);
  }

  public get state(): string {
    return this.fsm.state || '';
  }

  public trigger(event: string): void {
    this.fsm.trigger(event);
  }

  public update(elapsed: number): void {
    super.update(elapsed);

    this.maxAttackDT = 100 + (this.health / this.maxHealth) * 200;
    this.statefulAnimation.ATTACK1.animationSpeed = 0.8 - (this.health / this.maxHealth) * 0.4;
    this.statefulAnimation.ATTACK2.animationSpeed = 0.5 - (this.health / this.maxHealth) * 0.25;

    if (this.health < this.maxHealth && (this.state === 'IDLE' || this.state === 'HIT')) {
      this.attackDT += elapsed;
    }
    if (this.attackDT >= this.maxAttackDT) {
      this.attackDT -= this.maxAttackDT;
      sound.play('/audio/boss_motion.mp3', { loop: false, volume: Store.instance.state.volume as number });

      if (this.attackProcess >= 3) {
        if (Math.random() < 0.5) {
          this.fsm.trigger('ATTACK1');
        } else {
          this.fsm.trigger('ATTACK2');
        }
        this.attackProcess = 4;
      } else if (this.attackProcess === 0) {
        this.fsm.trigger('ATTACK1');
        this.attackDT = 250;
        this.attackProcess = 1;
      } else if (this.attackProcess === 2) {
        this.fsm.trigger('ATTACK2');
        this.attackProcess = 3;
      }
    }
    this.progress.update(this.attackDT, this.maxAttackDT);
    this.fsm.update();
  }

  public hit(damage: number): void {
    this.vibrator.vibrate(24);
    this.emit('damaged', damage);
    if (this.health > damage) {
      this.health -= damage;
      this.fsm.trigger('HIT');
    } else {
      this.health = 0;
      this.fsm.trigger('DIE');
      this.emit('DIE');
    }
  }

  public vibrate(strength: number): void {
    this.vibrator.vibrate(strength);
  }

  private get states(): StateMap<IMonster> {
    return {
      IDLE: {
        DIE: [{ events: ['DIE'] }],
        LAST_HIT: [{ events: ['LAST_HIT'] }],
        ATTACK1: [{ events: ['ATTACK1'] }],
        ATTACK2: [{ events: ['ATTACK2'] }],
        HIT: [{ events: ['HIT'] }],
      },
      HIT: {
        DIE: [{ events: ['DIE'] }],
        LAST_HIT: [{ events: ['LAST_HIT'] }],
        ATTACK1: [{ events: ['ATTACK1'] }],
        ATTACK2: [{ events: ['ATTACK2'] }],
        IDLE: [{ events: ['ANIMATION_DONE'] }],
      },
      DIE: {},
      ATTACK1: {
        DIE: [{ events: ['DIE'] }],
        LAST_HIT: [{ events: ['LAST_HIT'] }],
        ATTACK1: [{ events: ['ATTACK1'] }],
        ATTACK2: [{ events: ['ATTACK2'] }],
        IDLE: [{ events: ['ANIMATION_DONE'] }],
      },
      ATTACK2: {
        DIE: [{ events: ['DIE'] }],
        LAST_HIT: [{ events: ['LAST_HIT'] }],
        ATTACK1: [{ events: ['ATTACK1'] }],
        ATTACK2: [{ events: ['ATTACK2'] }],
        IDLE: [{ events: ['ANIMATION_DONE'] }],
      },
      LAST_HIT: {
        DIE: [{ events: ['DIE'] }],
      },
    };
  }

  public transition(prevState: string, nextState: string): void {
    const info = this.statefulAnimation[nextState];
    const flipX = this.animatedSprite.scale.x < 0 ? -1 : 1;

    this.animatedSprite.textures = info.textures;
    this.animatedSprite.x = info.offset.x * flipX;
    this.animatedSprite.y = info.offset.y;
    this.animatedSprite.scale.set(flipX * Math.abs(this.animatedSprite.scale.x), this.animatedSprite.scale.y);
    this.animatedSprite.animationSpeed = info.animationSpeed;
    this.animatedSprite.loop = info.loop;

    this.animatedSprite.gotoAndPlay(0);
  }
}

export default Cucumber;
